import * as React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton, Box, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function FixedSizeDialog({
  open,
  handleClose,
  dialogHeading,
  content,
  negativeResponse = "Cancel",
  maxWidth = "md",
  scroll = "paper",
  // optional positive action
  handleSubmit,
  positiveResponse,
  isSubmitting = false,
  isSubmittingText,
}) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const bg      = theme.palette.background.alt;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      scroll={scroll}
      PaperProps={{
        sx: {
          backgroundColor: bg,
          border: `1px solid ${border}`,
          borderRadius: "14px",
          boxShadow: isDark
            ? "0 24px 48px rgba(0,0,0,0.5)"
            : "0 24px 48px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.35)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "1.5rem",
          py: "1rem",
          borderBottom: `1px solid ${border}`,
          backgroundColor: isDark
            ? "rgba(29,158,117,0.05)"
            : "rgba(29,158,117,0.04)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{
            width: "6px", height: "20px",
            borderRadius: "3px",
            backgroundColor: accent,
            flexShrink: 0,
          }} />
          <Typography sx={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "17px", fontWeight: 400,
            color: textPri, lineHeight: 1.3,
          }}>
            {dialogHeading}
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: textSec,
            borderRadius: "8px",
            border: `1px solid ${border}`,
            width: "30px", height: "30px",
            "&:hover": {
              backgroundColor: "rgba(226,75,74,0.08)",
              borderColor: "rgba(226,75,74,0.3)",
              color: "#e24b4a",
            },
            transition: "all 0.15s",
          }}
        >
          <CloseIcon sx={{ fontSize: "15px" }} />
        </IconButton>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent
        sx={{
          px: "1.5rem",
          py: "1.25rem",
          color: textPri,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            borderRadius: "4px",
          },
        }}
      >
        {content}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: "1.5rem",
          py: "0.875rem",
          borderTop: `1px solid ${border}`,
          backgroundColor: isDark
            ? "rgba(29,158,117,0.03)"
            : "rgba(29,158,117,0.02)",
          gap: "0.5rem",
          justifyContent: "flex-end",
        }}
      >
        {/* Cancel */}
        <Button
          onClick={handleClose}
          sx={{
            fontSize: "13px", fontWeight: 500,
            px: "1rem", py: "0.4rem",
            borderRadius: "8px", textTransform: "none",
            border: `1px solid ${border}`,
            color: textSec, backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "rgba(226,75,74,0.06)",
              borderColor: "rgba(226,75,74,0.3)",
              color: "#e24b4a",
            },
            transition: "all 0.15s",
          }}
        >
          {negativeResponse}
        </Button>

        {/* Optional confirm action */}
        {handleSubmit && positiveResponse && (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              fontSize: "13px", fontWeight: 500,
              px: "1rem", py: "0.4rem",
              borderRadius: "8px", textTransform: "none",
              backgroundColor: accent, color: "#fff",
              "&:hover": { backgroundColor: "#0F6E56" },
              "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
              transition: "all 0.15s",
            }}
          >
            {isSubmitting ? (isSubmittingText ?? "Please wait…") : positiveResponse}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}